import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import DataListPage from "../../../components/DataListPage";
import DataListDateRange from "../../../components/DataListDateRange";
import TruncatedCell from "../../../components/TruncatedCell";
import user2Service from "../../../services/user2Service";
import { instanceToken } from "../../../utils/constant";
import { formatDetailDate } from "../../../utils/detailUtils";

export default function Users2() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [users, setUsers] = useState(null);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ from: "", to: "" });

  const parse = useCallback((responseData) => {
    setUsers(responseData.demo_user);
    setCount(responseData.count);
  }, []);

  const fetchAllData = useCallback(async (searchValue, filterValues) => {
    try {
      const res = await user2Service.getAllUsersData(
        instanceToken.token,
        searchValue,
        filterValues
      );
      setData(res.data.demo_user);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchUsers = useCallback(
    async (limit, offset, searchValue, filterValues) => {
      try {
        const res = await user2Service.getAllUsers(
          instanceToken.token,
          limit,
          offset,
          searchValue,
          filterValues
        );
        parse(res.data);
      } catch (error) {
        console.error(error);
      }
    },
    [parse]
  );

  useEffect(() => {
    const offset = page * rowsPerPage;
    fetchAllData(search, filters);
    fetchUsers(rowsPerPage, offset, search, filters);
  }, [page, rowsPerPage, search, filters, fetchAllData, fetchUsers]);

  useEffect(() => {
    user2Service.searchUser(instanceToken.token, search).catch(console.error);
  }, [search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const exportRows = (rows) => {
    const dataToExport = rows.map((u) => ({
      UserID: u.userid,
      Name: u.name,
      User_Name: u.username,
      Gender: u.gender,
      Age: u.age,
      Phone: u.phone_number,
      Email: u.email,
      Address: u.address,
      Role: u.role,
      Created_Date: u.createdAt || u.created_at,
      Otp_Exp_Time: u.otp_exp_time,
      One_Time_Password: u.one_time_password,
    }));

    const xls = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, xls, "User Level 2");
    XLSX.writeFile(wb, `${new Date().toISOString()}.xlsx`);
  };

  const openDetail = (row) => navigate(`/users2/${row.userid}`);

  const columns = [
    { id: "userid", label: "User ID", render: (row) => row.userid },
    {
      id: "name",
      label: "Name",
      render: (row) => <TruncatedCell value={row.name} />,
    },
    {
      id: "username",
      label: "Username",
      render: (row) => <TruncatedCell value={row.username} />,
    },
    {
      id: "gender",
      label: "Gender",
      render: (row) => <TruncatedCell value={row.gender} />,
    },
    {
      id: "phone_number",
      label: "Phone",
      render: (row) => <TruncatedCell value={row.phone_number} />,
    },
    {
      id: "email",
      label: "Email",
      render: (row) => <TruncatedCell value={row.email} />,
    },
    {
      id: "address",
      label: "Address",
      render: (row) => <TruncatedCell value={row.address} />,
    },
    {
      id: "created_at",
      label: "Created Date",
      render: (row) =>
        formatDetailDate(row.createdAt || row.created_at) || "N/A",
    },
  ];

  return (
    <DataListPage
      title="User Level 2"
      rows={users || []}
      columns={columns}
      count={count}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      headerActions={[
        { label: "Export All Excel", onClick: () => exportRows(data) },
        { label: "Export to Excel", onClick: () => exportRows(users || []) },
      ]}
      search={{
        placeholder: "Search by username, name, email, phone...",
        onSearch: setSearch,
        live: true,
        showFilterButton: false,
      }}
      toolbarExtra={
        <DataListDateRange
          from={filters.from}
          to={filters.to}
          onChange={(next) => {
            setFilters(next);
            setPage(0);
          }}
          onClear={() => {
            setFilters({ from: "", to: "" });
            setSearch("");
            setPage(0);
          }}
        />
      }
      onView={openDetail}
      menuActions={[{ label: "View details", onClick: openDetail }]}
      getRowKey={(row) => row.userid}
      loading={!users}
    />
  );
}
